import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { Alert, Button, DetailList, Dialog, Field } from "../../ui";
import { failure } from "../workspace/behavior";
import { bankName, businessCodeLabel, money } from "../workspace/model";
import { cancelTransfer, executeTransfer, getTransferDetail } from "./api";

export function TransferActionDialog({
  transferId,
  kind,
  onClose,
}: {
  readonly transferId: number;
  readonly kind: "cancel" | "retry";
  readonly onClose: () => void;
}) {
  const { apiClient, queryClient } = useAdminApplication();
  const session = useAdminSession();
  const cacheKey = [
    "admin",
    session.sessionEpoch,
    "transfer-command",
    transferId,
    kind,
  ] as const;
  const previous = queryClient.getQueryData<{
    key: string;
    reason: string;
    unknown: boolean;
    attemptSnapshot?: string;
  }>(cacheKey);
  const [reason, setReason] = useState(previous?.reason ?? "");
  const [pending, setPending] = useState(false);
  const [unknown, setUnknown] = useState(previous?.unknown ?? false);
  const [message, setMessage] = useState("");
  const [done, setDone] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const request = useRef(previous);
  const busy = useRef(false);
  const detail = useQuery({
    queryKey: [
      "admin",
      session.sessionEpoch,
      "transfer-command-detail",
      transferId,
    ],
    queryFn: ({ signal }) => getTransferDetail(apiClient, transferId, signal),
    staleTime: 0,
    retry: false,
  });
  const row = detail.data;
  const action = row?.availableActions?.find(
    (a) =>
      a.code === (kind === "cancel" ? "TRANSFER_CANCEL" : "TRANSFER_RETRY"),
  );
  const allowed = Boolean(
    action?.allowed &&
    session.authSession?.authorities?.includes("TRANSFER_EXECUTE") &&
    (kind !== "retry" || row?.lastErrorCode !== "ADMIN_CANCEL"),
  );
  const refresh = async () => {
    const latest = (await detail.refetch()).data;
    if (!latest) return;
    if (
      latest.status === "COMPLETED" ||
      (latest.status === "FAILED" && latest.lastErrorCode === "ADMIN_CANCEL")
    ) {
      setUnknown(false);
      setDone(true);
      setSucceeded(
        kind === "retry"
          ? latest.status === "COMPLETED"
          : latest.lastErrorCode === "ADMIN_CANCEL",
      );
      queryClient.setQueryData(
        cacheKey,
        request.current ? { ...request.current, unknown: false } : undefined,
      );
      setMessage(
        latest.status === "COMPLETED"
          ? "송금이 완료되었습니다. 같은 송금을 다시 요청하지 마세요."
          : kind === "cancel"
            ? "대기 송금이 취소되었습니다."
            : "관리자가 취소한 송금입니다. 재시도할 수 없습니다.",
      );
      return;
    }
    const changed =
      JSON.stringify(latest.attempts) !== request.current?.attemptSnapshot;
    const finalAttempt = latest.attempts.at(-1);
    if (
      kind === "retry" &&
      latest.status === "FAILED" &&
      changed &&
      finalAttempt?.status === "FAILED" &&
      finalAttempt.respondedAt
    ) {
      setUnknown(false);
      queryClient.setQueryData(
        cacheKey,
        request.current ? { ...request.current, unknown: false } : undefined,
      );
      setMessage(
        "새 송금 시도가 실패한 것으로 확인됐습니다. 실패 사유와 재시도 가능 여부를 확인해 주세요.",
      );
    } else
      setMessage(
        "처리 결과가 아직 확정되지 않았습니다. 최신 상태를 다시 조회해 주세요.",
      );
  };
  const submit = async () => {
    if (busy.current || !allowed || unknown || done) return;
    const text = reason.trim();
    if (kind === "cancel" && (text.length < 5 || text.length > 1000)) {
      setMessage("취소 사유를 5~1,000자로 입력해 주세요.");
      return;
    }
    // Each explicit retry starts from the latest attempt list. An older failure
    // must never release the guard for a newer request whose result is unknown.
    const nextRequest = {
      key: globalThis.crypto.randomUUID(),
      reason: text,
      unknown: true,
      attemptSnapshot: JSON.stringify(row?.attempts),
    };
    request.current =
      kind === "retry" ? nextRequest : (request.current ?? nextRequest);
    queryClient.setQueryData(cacheKey, { ...request.current, unknown: true });
    busy.current = true;
    setPending(true);
    setMessage("");
    try {
      const result =
        kind === "cancel"
          ? await cancelTransfer(
              apiClient,
              transferId,
              request.current.reason,
              request.current.key,
            )
          : await executeTransfer(apiClient, transferId);
      const canceled =
        kind === "cancel" &&
        result.status === "FAILED" &&
        "lastErrorCode" in result &&
        result.lastErrorCode === "ADMIN_CANCEL";
      const completed = kind === "retry" && result.status === "COMPLETED";
      if (canceled || completed) {
        setDone(true);
        setSucceeded(true);
        setUnknown(false);
        queryClient.setQueryData(cacheKey, {
          ...request.current,
          unknown: false,
        });
        setMessage(
          canceled ? "대기 송금이 취소되었습니다." : "송금이 완료되었습니다.",
        );
      } else {
        const failed = ["FAILED", "TRANSFER_FAILED"].includes(result.status);
        setUnknown(!failed);
        queryClient.setQueryData(cacheKey, {
          ...request.current,
          unknown: !failed,
        });
        setMessage(
          failed
            ? "송금 재시도가 실패했습니다. 실패 사유를 확인해 주세요."
            : "처리 결과를 확인해야 합니다. 다시 요청하지 말고 최신 상태를 조회해 주세요.",
        );
      }
    } catch (error) {
      const status = (error as { status?: number }).status;
      const uncertain =
        failure(error).reconcile ||
        !(status && [400, 401, 403, 404, 422].includes(status));
      setUnknown(uncertain);
      queryClient.setQueryData(cacheKey, {
        ...request.current,
        unknown: uncertain,
      });
      setMessage(failure(error).message);
    } finally {
      busy.current = false;
      setPending(false);
      await queryClient.invalidateQueries({
        queryKey: ["admin", session.sessionEpoch],
      });
    }
  };
  return (
    <Dialog
      open
      title={
        kind === "cancel"
          ? "대기 송금을 취소할까요?"
          : "실패 송금을 재시도할까요?"
      }
      onClose={onClose}
      closeDisabled={pending}
      size="medium"
      primaryAction={
        !done && !unknown ? (
          <Button
            size="small"
            isLoading={pending}
            disabled={
              !allowed ||
              detail.isFetching ||
              (kind === "cancel" && reason.trim().length < 5)
            }
            onClick={() => {
              void submit();
            }}
          >
            {kind === "cancel" ? "송금 취소 확정" : "재시도 확정"}
          </Button>
        ) : undefined
      }
      secondaryAction={
        <Button
          variant="quiet"
          size="small"
          disabled={pending}
          onClick={onClose}
        >
          닫기
        </Button>
      }
    >
      {detail.isPending ? (
        <p role="status">최신 송금 상태를 확인하는 중입니다.</p>
      ) : detail.isError ? (
        <Alert
          persistent
          variant="danger"
          title="송금 조회 실패"
          action={
            <Button
              variant="quiet"
              size="small"
              onClick={() => {
                void detail.refetch();
              }}
            >
              다시 조회
            </Button>
          }
        >
          송금 정보를 확인한 뒤 처리해 주세요.
        </Alert>
      ) : row ? (
        <DetailList
          items={[
            { label: "거래ID", value: row.displayTransactionNo ?? "정보 없음" },
            {
              label: "은행·계좌",
              value: `${row.bankName ?? bankName(row.bankCode)} · ${row.accountNumberMasked}`,
            },
            { label: "예금주", value: row.accountHolderMasked },
            { label: "금액", value: money(row.requestedAmount) },
            {
              label: "실패 사유",
              value:
                row.lastErrorCode === "ADMIN_CANCEL"
                  ? "관리자 취소"
                  : businessCodeLabel(row.lastErrorCode),
            },
          ]}
        />
      ) : null}
      {!allowed && row ? (
        <p>
          {action?.blockedReasonMessage ??
            "현재 권한과 송금 상태에서는 처리할 수 없습니다."}
        </p>
      ) : null}
      {kind === "cancel" ? (
        <Field
          label="취소 사유"
          control="textarea"
          maxLength={1000}
          value={reason}
          disabled={pending || Boolean(request.current)}
          onChange={(event) => setReason(event.currentTarget.value)}
        />
      ) : (
        <p>
          은행·계좌·예금주와 실패 사유를 확인했습니다. 기존 실패 송금을 다시
          요청합니다.
        </p>
      )}
      {message ? (
        <Alert
          persistent
          variant={succeeded ? "success" : "warning"}
          title={
            succeeded
              ? "처리 완료"
              : unknown
                ? "처리 결과 확인 필요"
                : "처리 안내"
          }
        >
          {message}
        </Alert>
      ) : null}
      {unknown || (message && !done) ? (
        <Button
          size="small"
          variant="secondary"
          disabled={pending || detail.isFetching}
          onClick={() => {
            void refresh();
          }}
        >
          최신 상태 조회
        </Button>
      ) : null}
    </Dialog>
  );
}
